export const DISPATCH_COLOR_INTENSITY_MIN = -20;
export const DISPATCH_COLOR_INTENSITY_MAX = 60;
export const DEFAULT_DISPATCH_COLOR_INTENSITY_PERCENT = 0;
export const DEFAULT_PROTECT_AFTER_REASSIGNMENT = true;
export const DEFAULT_DISPATCH_REASSIGNMENT_MODE = "complete";
export const DEFAULT_ROUTE_MAP_GROUP_RANGES_VISIBLE = false;
export const DEFAULT_OPTIMIZATION_ANIMATION_ENABLED = true;
export const DEFAULT_LAST_DISPATCH_DATE = "";
export const DEFAULT_LAST_CONSOLIDATION_FROM_DATE = "";
export const DEFAULT_LAST_CONSOLIDATION_TO_DATE = "";
export const DEFAULT_SIDEBAR_COLLAPSED = false;
export const MAX_DISMISSED_PRODUCT_UPDATE_IDS = 500;

export const USER_UI_PREFERENCES_CACHE_STORAGE_KEY = "grain-user-ui-preferences-cache-v1";
export const USER_UI_PREFERENCES_CACHE_VERSION = 8;
export const USER_UI_PREFERENCES_CACHE_MAX_USERS = 8;

export const CONSOLIDATION_DISPLAY_PREFERENCE_KEYS = Object.freeze(["consolidation_hide_arrival", "consolidation_show_planned", "consolidation_hide_timing", "consolidation_show_order", "consolidation_show_billed", "consolidation_show_hours_summary"]);

export const DEFAULT_USER_UI_PREFERENCES = Object.freeze({
  consolidation_hide_arrival: true,
  consolidation_show_planned: false,
  consolidation_hide_timing: true,
  consolidation_show_order: false,
  consolidation_show_billed: true,
  consolidation_show_hours_summary: false,

  dispatch_color_intensity_percent: DEFAULT_DISPATCH_COLOR_INTENSITY_PERCENT,
  protect_after_reassignment: DEFAULT_PROTECT_AFTER_REASSIGNMENT,
  dispatch_reassignment_mode: DEFAULT_DISPATCH_REASSIGNMENT_MODE,
  route_map_group_ranges_visible: DEFAULT_ROUTE_MAP_GROUP_RANGES_VISIBLE,
  optimization_animation_enabled: DEFAULT_OPTIMIZATION_ANIMATION_ENABLED,
  last_dispatch_date: DEFAULT_LAST_DISPATCH_DATE,
  last_consolidation_from_date: DEFAULT_LAST_CONSOLIDATION_FROM_DATE,
  last_consolidation_to_date: DEFAULT_LAST_CONSOLIDATION_TO_DATE,
  sidebar_collapsed: DEFAULT_SIDEBAR_COLLAPSED,
  dismissed_product_update_ids: Object.freeze([]),
});

export const USER_UI_PREFERENCE_KEYS = Object.freeze([
  ...CONSOLIDATION_DISPLAY_PREFERENCE_KEYS,
  "dispatch_color_intensity_percent",
  "protect_after_reassignment",
  "dispatch_reassignment_mode",
  "route_map_group_ranges_visible",
  "optimization_animation_enabled",
  "last_dispatch_date",
  "last_consolidation_from_date",
  "last_consolidation_to_date",
  "sidebar_collapsed",
  "dismissed_product_update_ids",
]);

const USER_UI_PREFERENCE_KEY_SET = new Set(USER_UI_PREFERENCE_KEYS);

function isObjectRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function getDefaultWindow() {
  return typeof window !== "undefined" ? window : undefined;
}

function getLocalStorage(targetWindow = getDefaultWindow()) {
  try {
    return targetWindow?.localStorage ?? null;
  } catch {
    return null;
  }
}

function normalizeFallbackPercent(value) {
  return isValidDispatchColorIntensityPercent(value)
    ? value
    : DEFAULT_DISPATCH_COLOR_INTENSITY_PERCENT;
}

export function isValidDispatchColorIntensityPercent(value) {
  return Number.isInteger(value)
    && value >= DISPATCH_COLOR_INTENSITY_MIN
    && value <= DISPATCH_COLOR_INTENSITY_MAX;
}

export function normalizeDispatchColorIntensityPercent(
  value,
  fallback = DEFAULT_DISPATCH_COLOR_INTENSITY_PERCENT,
) {
  const normalizedFallback = normalizeFallbackPercent(fallback);
  const candidate = typeof value === "string" && value.trim()
    ? Number(value)
    : value;

  if (!Number.isFinite(candidate)) {
    return normalizedFallback;
  }

  return Math.min(
    DISPATCH_COLOR_INTENSITY_MAX,
    Math.max(DISPATCH_COLOR_INTENSITY_MIN, Math.round(candidate)),
  );
}

export function isValidDispatchReassignmentMode(value) {
  return value === "complete" || value === "fast";
}

export function normalizeDispatchReassignmentModePreference(
  value,
  fallback = DEFAULT_DISPATCH_REASSIGNMENT_MODE,
) {
  return isValidDispatchReassignmentMode(value)
    ? value
    : (isValidDispatchReassignmentMode(fallback) ? fallback : DEFAULT_DISPATCH_REASSIGNMENT_MODE);
}

export function isValidLastDispatchDate(value) {
  if (value === "") {
    return true;
  }
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const year = Number(value.slice(0, 4));
  const parsedDate = new Date(`${value}T00:00:00.000Z`);
  return year >= 1
    && !Number.isNaN(parsedDate.getTime())
    && parsedDate.toISOString().slice(0, 10) === value;
}

export function normalizeLastDispatchDatePreference(
  value,
  fallback = DEFAULT_LAST_DISPATCH_DATE,
) {
  const normalizedFallback = isValidLastDispatchDate(fallback)
    ? fallback
    : DEFAULT_LAST_DISPATCH_DATE;
  return isValidLastDispatchDate(value) ? value : normalizedFallback;
}

export function isValidLastConsolidationDate(value) {
  return isValidLastDispatchDate(value);
}

export function normalizeLastConsolidationDatePreference(
  value,
  fallback = DEFAULT_LAST_CONSOLIDATION_FROM_DATE,
) {
  const normalizedFallback = isValidLastConsolidationDate(fallback)
    ? fallback
    : DEFAULT_LAST_CONSOLIDATION_FROM_DATE;
  return isValidLastConsolidationDate(value) ? value : normalizedFallback;
}

export function normalizeDismissedProductUpdateIds(values, { strict = false } = {}) {
  if (!Array.isArray(values)) {
    if (strict) {
      throw new TypeError("dismissed_product_update_ids doit être une liste.");
    }
    return [];
  }

  const normalizedIds = [];
  const seenIds = new Set();
  for (const value of values) {
    if (typeof value !== "string") {
      if (strict) {
        throw new TypeError("Chaque identifiant de mise à jour ignorée doit être une chaîne.");
      }
      continue;
    }
    const updateId = value.trim();
    if (!updateId || updateId.length > 512) {
      if (strict) {
        throw new TypeError("Chaque identifiant de mise à jour ignorée doit contenir de 1 à 512 caractères.");
      }
      continue;
    }
    if (!seenIds.has(updateId)) {
      seenIds.add(updateId);
      normalizedIds.push(updateId);
    }
  }

  if (strict && normalizedIds.length > MAX_DISMISSED_PRODUCT_UPDATE_IDS) {
    throw new TypeError(
      `dismissed_product_update_ids ne peut pas dépasser ${MAX_DISMISSED_PRODUCT_UPDATE_IDS} éléments.`,
    );
  }
  return normalizedIds.slice(0, MAX_DISMISSED_PRODUCT_UPDATE_IDS);
}

export function normalizeUserUiPreferences(payload, {
  fallback = DEFAULT_USER_UI_PREFERENCES,
} = {}) {
  const fallbackPercent = normalizeFallbackPercent(
    fallback?.dispatch_color_intensity_percent,
  );
  const percent = isValidDispatchColorIntensityPercent(
    payload?.dispatch_color_intensity_percent,
  )
    ? payload.dispatch_color_intensity_percent
    : fallbackPercent;
  const fallbackProtection = typeof fallback?.protect_after_reassignment === "boolean"
    ? fallback.protect_after_reassignment
    : DEFAULT_PROTECT_AFTER_REASSIGNMENT;
  const protectAfterReassignment = typeof payload?.protect_after_reassignment === "boolean"
    ? payload.protect_after_reassignment
    : fallbackProtection;
  const dispatchReassignmentMode = normalizeDispatchReassignmentModePreference(
    payload?.dispatch_reassignment_mode,
    fallback?.dispatch_reassignment_mode,
  );
  const fallbackRouteMapGroupRangesVisible = typeof fallback?.route_map_group_ranges_visible === "boolean"
    ? fallback.route_map_group_ranges_visible
    : DEFAULT_ROUTE_MAP_GROUP_RANGES_VISIBLE;
  const routeMapGroupRangesVisible = typeof payload?.route_map_group_ranges_visible === "boolean"
    ? payload.route_map_group_ranges_visible
    : fallbackRouteMapGroupRangesVisible;
  const fallbackOptimizationAnimationEnabled = typeof fallback?.optimization_animation_enabled === "boolean"
    ? fallback.optimization_animation_enabled
    : DEFAULT_OPTIMIZATION_ANIMATION_ENABLED;
  const optimizationAnimationEnabled = typeof payload?.optimization_animation_enabled === "boolean"
    ? payload.optimization_animation_enabled
    : fallbackOptimizationAnimationEnabled;
  const lastDispatchDate = normalizeLastDispatchDatePreference(
    payload?.last_dispatch_date,
    fallback?.last_dispatch_date,
  );
  const lastConsolidationFromDate = normalizeLastConsolidationDatePreference(
    payload?.last_consolidation_from_date,
    fallback?.last_consolidation_from_date,
  );
  const lastConsolidationToDate = normalizeLastConsolidationDatePreference(
    payload?.last_consolidation_to_date,
    fallback?.last_consolidation_to_date,
  );
  const fallbackSidebarCollapsed = typeof fallback?.sidebar_collapsed === "boolean"
    ? fallback.sidebar_collapsed
    : DEFAULT_SIDEBAR_COLLAPSED;
  const sidebarCollapsed = typeof payload?.sidebar_collapsed === "boolean"
    ? payload.sidebar_collapsed
    : fallbackSidebarCollapsed;
  const dismissedProductUpdateIds = Array.isArray(payload?.dismissed_product_update_ids)
    ? normalizeDismissedProductUpdateIds(payload.dismissed_product_update_ids)
    : normalizeDismissedProductUpdateIds(fallback?.dismissed_product_update_ids);

  return {
    ...Object.fromEntries(CONSOLIDATION_DISPLAY_PREFERENCE_KEYS.map(key => [key,
      typeof payload?.[key] === "boolean" ? payload[key] : fallback?.[key] === true])),
    dispatch_color_intensity_percent: percent,
    protect_after_reassignment: protectAfterReassignment,
    dispatch_reassignment_mode: dispatchReassignmentMode,
    route_map_group_ranges_visible: routeMapGroupRangesVisible,
    optimization_animation_enabled: optimizationAnimationEnabled,
    last_dispatch_date: lastDispatchDate,
    last_consolidation_from_date: lastConsolidationFromDate,
    last_consolidation_to_date: lastConsolidationToDate,
    sidebar_collapsed: sidebarCollapsed,
    dismissed_product_update_ids: dismissedProductUpdateIds,
  };
}

export function assertValidUserUiPreferencesPayload(payload) {
  if (!isObjectRecord(payload)) {
    throw new TypeError("Les préférences d’interface reçues sont invalides.");
  }

  for (const key of CONSOLIDATION_DISPLAY_PREFERENCE_KEYS) {
    if (Object.hasOwn(payload, key) && typeof payload[key] !== "boolean") {
      throw new TypeError(`${key} doit être un booléen.`);
    }
  }

  const unknownKeys = Object.keys(payload).filter((key) => !USER_UI_PREFERENCE_KEY_SET.has(key));
  if (unknownKeys.length > 0) {
    throw new TypeError(`Préférence d’interface inconnue : ${unknownKeys[0]}.`);
  }

  if (!Object.hasOwn(payload, "dispatch_color_intensity_percent")) {
    throw new TypeError("La préférence dispatch_color_intensity_percent est manquante.");
  }

  if (!Object.hasOwn(payload, "protect_after_reassignment")) {
    throw new TypeError("La préférence protect_after_reassignment est manquante.");
  }

  if (!Object.hasOwn(payload, "dispatch_reassignment_mode")) {
    throw new TypeError("La préférence dispatch_reassignment_mode est manquante.");
  }

  if (!Object.hasOwn(payload, "route_map_group_ranges_visible")) {
    throw new TypeError("La préférence route_map_group_ranges_visible est manquante.");
  }

  if (!Object.hasOwn(payload, "optimization_animation_enabled")) {
    throw new TypeError("La préférence optimization_animation_enabled est manquante.");
  }

  if (!Object.hasOwn(payload, "last_dispatch_date")) {
    throw new TypeError("La préférence last_dispatch_date est manquante.");
  }

  if (!Object.hasOwn(payload, "last_consolidation_from_date")) {
    throw new TypeError("La préférence last_consolidation_from_date est manquante.");
  }

  if (!Object.hasOwn(payload, "last_consolidation_to_date")) {
    throw new TypeError("La préférence last_consolidation_to_date est manquante.");
  }

  if (!Object.hasOwn(payload, "sidebar_collapsed")) {
    throw new TypeError("La préférence sidebar_collapsed est manquante.");
  }

  if (!Object.hasOwn(payload, "dismissed_product_update_ids")) {
    throw new TypeError("La préférence dismissed_product_update_ids est manquante.");
  }

  if (!isValidDispatchColorIntensityPercent(payload.dispatch_color_intensity_percent)) {
    throw new TypeError(
      `dispatch_color_intensity_percent doit être un entier entre ${DISPATCH_COLOR_INTENSITY_MIN} et ${DISPATCH_COLOR_INTENSITY_MAX}.`,
    );
  }

  if (typeof payload.protect_after_reassignment !== "boolean") {
    throw new TypeError("protect_after_reassignment doit être un booléen.");
  }

  if (!isValidDispatchReassignmentMode(payload.dispatch_reassignment_mode)) {
    throw new TypeError("dispatch_reassignment_mode doit valoir complete ou fast.");
  }

  if (typeof payload.route_map_group_ranges_visible !== "boolean") {
    throw new TypeError("route_map_group_ranges_visible doit être un booléen.");
  }

  if (typeof payload.optimization_animation_enabled !== "boolean") {
    throw new TypeError("optimization_animation_enabled doit être un booléen.");
  }

  if (typeof payload.sidebar_collapsed !== "boolean") {
    throw new TypeError("sidebar_collapsed doit être un booléen.");
  }

  if (!isValidLastDispatchDate(payload.last_dispatch_date)) {
    throw new TypeError("last_dispatch_date doit être vide ou respecter le format YYYY-MM-DD.");
  }

  if (!isValidLastConsolidationDate(payload.last_consolidation_from_date)) {
    throw new TypeError("last_consolidation_from_date doit être vide ou respecter le format YYYY-MM-DD.");
  }

  if (!isValidLastConsolidationDate(payload.last_consolidation_to_date)) {
    throw new TypeError("last_consolidation_to_date doit être vide ou respecter le format YYYY-MM-DD.");
  }

  normalizeDismissedProductUpdateIds(payload.dismissed_product_update_ids, { strict: true });

  return normalizeUserUiPreferences(payload);
}

export function assertValidUserUiPreferencesPatch(patch) {
  if (!isObjectRecord(patch)) {
    throw new TypeError("La mise à jour des préférences d’interface est invalide.");
  }

  for (const key of CONSOLIDATION_DISPLAY_PREFERENCE_KEYS) {
    if (Object.hasOwn(patch, key) && typeof patch[key] !== "boolean") {
      throw new TypeError(`${key} doit être un booléen.`);
    }
  }

  const keys = Object.keys(patch);
  if (keys.length === 0) {
    throw new TypeError("La mise à jour des préférences d’interface est vide.");
  }

  const unknownKeys = keys.filter((key) => !USER_UI_PREFERENCE_KEY_SET.has(key));
  if (unknownKeys.length > 0) {
    throw new TypeError(`Préférence d’interface inconnue : ${unknownKeys[0]}.`);
  }

  if (
    Object.hasOwn(patch, "dispatch_color_intensity_percent")
    && !isValidDispatchColorIntensityPercent(patch.dispatch_color_intensity_percent)
  ) {
    throw new TypeError(
      `dispatch_color_intensity_percent doit être un entier entre ${DISPATCH_COLOR_INTENSITY_MIN} et ${DISPATCH_COLOR_INTENSITY_MAX}.`,
    );
  }

  if (
    Object.hasOwn(patch, "protect_after_reassignment")
    && typeof patch.protect_after_reassignment !== "boolean"
  ) {
    throw new TypeError("protect_after_reassignment doit être un booléen.");
  }

  if (
    Object.hasOwn(patch, "dispatch_reassignment_mode")
    && !isValidDispatchReassignmentMode(patch.dispatch_reassignment_mode)
  ) {
    throw new TypeError("dispatch_reassignment_mode doit valoir complete ou fast.");
  }

  if (
    Object.hasOwn(patch, "route_map_group_ranges_visible")
    && typeof patch.route_map_group_ranges_visible !== "boolean"
  ) {
    throw new TypeError("route_map_group_ranges_visible doit être un booléen.");
  }

  if (
    Object.hasOwn(patch, "optimization_animation_enabled")
    && typeof patch.optimization_animation_enabled !== "boolean"
  ) {
    throw new TypeError("optimization_animation_enabled doit être un booléen.");
  }

  if (
    Object.hasOwn(patch, "last_dispatch_date")
    && !isValidLastDispatchDate(patch.last_dispatch_date)
  ) {
    throw new TypeError("last_dispatch_date doit être vide ou respecter le format YYYY-MM-DD.");
  }

  if (
    Object.hasOwn(patch, "last_consolidation_from_date")
    && !isValidLastConsolidationDate(patch.last_consolidation_from_date)
  ) {
    throw new TypeError("last_consolidation_from_date doit être vide ou respecter le format YYYY-MM-DD.");
  }

  if (
    Object.hasOwn(patch, "last_consolidation_to_date")
    && !isValidLastConsolidationDate(patch.last_consolidation_to_date)
  ) {
    throw new TypeError("last_consolidation_to_date doit être vide ou respecter le format YYYY-MM-DD.");
  }

  if (
    Object.hasOwn(patch, "sidebar_collapsed")
    && typeof patch.sidebar_collapsed !== "boolean"
  ) {
    throw new TypeError("sidebar_collapsed doit être un booléen.");
  }

  if (Object.hasOwn(patch, "dismissed_product_update_ids")) {
    return {
      ...patch,
      dismissed_product_update_ids: normalizeDismissedProductUpdateIds(
        patch.dismissed_product_update_ids,
        { strict: true },
      ),
    };
  }

  return { ...patch };
}

export function resolveUserUiPreferencesUserId(sessionOrUserId) {
  if (typeof sessionOrUserId === "string") {
    return sessionOrUserId.trim();
  }

  return typeof sessionOrUserId?.user?.id === "string"
    ? sessionOrUserId.user.id.trim()
    : "";
}

function createEmptyCacheRoot() {
  return {
    users: {},
    version: USER_UI_PREFERENCES_CACHE_VERSION,
  };
}

function parseCacheRoot(rawValue) {
  if (typeof rawValue !== "string" || !rawValue.trim()) {
    return createEmptyCacheRoot();
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (
      !isObjectRecord(parsed)
      || parsed.version !== USER_UI_PREFERENCES_CACHE_VERSION
      || !isObjectRecord(parsed.users)
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function parseCacheEntry(entry) {
  if (!isObjectRecord(entry) || !Number.isFinite(entry.saved_at)) {
    return null;
  }

  try {
    return {
      preferences: assertValidUserUiPreferencesPayload(entry.preferences),
      savedAt: Number(entry.saved_at),
    };
  } catch {
    return null;
  }
}

function writeCacheRoot(storage, cacheRoot) {
  try {
    if (Object.keys(cacheRoot.users).length === 0) {
      storage.removeItem(USER_UI_PREFERENCES_CACHE_STORAGE_KEY);
    } else {
      storage.setItem(USER_UI_PREFERENCES_CACHE_STORAGE_KEY, JSON.stringify(cacheRoot));
    }
    return true;
  } catch {
    return false;
  }
}

function readCacheRoot(storage) {
  let cacheRoot = null;
  try {
    cacheRoot = parseCacheRoot(storage.getItem(USER_UI_PREFERENCES_CACHE_STORAGE_KEY));
  } catch {
    cacheRoot = null;
  }

  if (cacheRoot) {
    return cacheRoot;
  }

  try {
    storage.removeItem(USER_UI_PREFERENCES_CACHE_STORAGE_KEY);
  } catch {}
  return createEmptyCacheRoot();
}

export function readUserUiPreferencesCache(userId, { targetWindow } = {}) {
  const resolvedUserId = resolveUserUiPreferencesUserId(userId);
  const storage = getLocalStorage(targetWindow);
  if (!resolvedUserId || !storage) {
    return null;
  }

  const cacheRoot = readCacheRoot(storage);
  if (!Object.hasOwn(cacheRoot.users, resolvedUserId)) {
    return null;
  }

  const parsedEntry = parseCacheEntry(cacheRoot.users[resolvedUserId]);
  if (!parsedEntry) {
    delete cacheRoot.users[resolvedUserId];
    writeCacheRoot(storage, cacheRoot);
    return null;
  }

  return {
    ...parsedEntry,
    userId: resolvedUserId,
  };
}

export function writeUserUiPreferencesCache(userId, preferences, {
  now = Date.now(),
  targetWindow,
} = {}) {
  const resolvedUserId = resolveUserUiPreferencesUserId(userId);
  const storage = getLocalStorage(targetWindow);
  if (!resolvedUserId || !storage || !Number.isFinite(now)) {
    return null;
  }

  let normalizedPreferences;
  try {
    normalizedPreferences = assertValidUserUiPreferencesPayload(preferences);
  } catch {
    return null;
  }

  const cacheRoot = readCacheRoot(storage);
  cacheRoot.users[resolvedUserId] = {
    preferences: normalizedPreferences,
    saved_at: Number(now),
  };

  const validEntries = Object.entries(cacheRoot.users)
    .map(([cachedUserId, entry]) => ({
      cachedUserId,
      entry,
      parsed: parseCacheEntry(entry),
    }))
    .filter(({ parsed }) => Boolean(parsed))
    .sort((left, right) => right.parsed.savedAt - left.parsed.savedAt)
    .slice(0, USER_UI_PREFERENCES_CACHE_MAX_USERS);

  cacheRoot.users = Object.fromEntries(
    validEntries.map(({ cachedUserId, entry }) => [cachedUserId, entry]),
  );

  if (!writeCacheRoot(storage, cacheRoot)) {
    return null;
  }

  return {
    preferences: normalizedPreferences,
    savedAt: Number(now),
    userId: resolvedUserId,
  };
}

export function clearUserUiPreferencesCache({ userId = "", targetWindow } = {}) {
  const storage = getLocalStorage(targetWindow);
  if (!storage) {
    return;
  }

  const resolvedUserId = resolveUserUiPreferencesUserId(userId);
  if (!resolvedUserId) {
    try {
      storage.removeItem(USER_UI_PREFERENCES_CACHE_STORAGE_KEY);
    } catch {}
    return;
  }

  const cacheRoot = readCacheRoot(storage);
  delete cacheRoot.users[resolvedUserId];
  writeCacheRoot(storage, cacheRoot);
}
